import { useWatch } from 'react-hook-form';
import { useCalendarService } from '@mews-commander/ui';
import { useDateFieldTransform } from '@mews/framework';
import { DateIntervalFieldsProps } from './types';

type Props = Pick<
    DateIntervalFieldsProps,
    'minDate' | 'minDayDifference' | 'maxDayDifference' | 'maxDate' | 'mode' | 'startDateProps' | 'endDateProps'
>;

export const useCreateDateIntervalRestrictions = ({
    minDate,
    maxDate,
    minDayDifference,
    maxDayDifference,
    mode,
    startDateProps,
    endDateProps,
}: Props) => {
    const calendarService = useCalendarService();
    const { name: startDateName } = startDateProps;
    const { name: endDateName } = endDateProps;

    const endDateValue = useWatch({ name: endDateName });
    const startDateValue = useWatch({ name: startDateName });

    const transformDate = useDateFieldTransform(mode);

    const startDateOutput = transformDate?.output?.(startDateValue);
    const startDateBase =
        typeof startDateOutput === 'string' && calendarService.isValidDateString(startDateOutput)
            ? calendarService.getDateFromDateString(startDateOutput)
            : undefined;

    const endDateBase =
        minDate && startDateBase ? calendarService.getMaxDate([minDate, startDateBase]) : minDate || startDateBase;

    const minEndDate =
        endDateBase && minDayDifference ? calendarService.addDays(endDateBase, minDayDifference) : endDateBase;
    const maxEndDate =
        startDateBase && maxDayDifference ? calendarService.addDays(startDateBase, maxDayDifference) : maxDate;

    return {
        minEndDate,
        maxEndDate,
        startDateValue: transformDate?.output?.(startDateValue),
        endDateValue: transformDate?.output?.(endDateValue),
    };
};
